import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { MtGroup, MtProjectGroup } from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    GroupFilters,
    ProjectGroupFilters,
    SortGroups,
    SortProjectGroups,
    setAccountPermissionsGroupIds,
    setAccountPermissionsProjectGroupIds,
    setAccountPermissionsGroupSort,
    setAccountPermissionsProjectGroupSort,
    setAccountPermissionsGroupFilters,
    setAccountPermissionsProjectGroupFilters,
    setAccountPermissionsGroupSelected,
    setAccountPermissionsProjectGroupSelected,
    updateBaseProjectPermission,
    closeModal,
} from "client/store"
import {
    PermissionType,
    ProjectPermissionItem,
} from "client/store/utils"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    TableSearchWrapper,
    Table,
    SortRows,
    PageForm,
    Button,
} from "client/components"

import GroupSearch from "client/pages/search/groupSearch"
import ProjectGroupSearch from "client/pages/search/projectGroupSearch"
import GroupColumns from "./groupColumns"
import ProjectGroupColumns from "./projectGroupColumns"

const ACCOUNT_GROUPS = "accountGroups"
const PROJECT_GROUPS = "projectGroups"

interface NotAssignedProps {
    accountId: number
    section: string
    permissionId: string
    permissionType: PermissionType
    permissionItem: ProjectPermissionItem
}

const NotAssigned = ({
    accountId = null,
    section,
    permissionId,
    permissionType,
    permissionItem,
}: NotAssignedProps) => {
    const groupList = useSelector((state: StoreType) => state.group.groupList)
    const projectGroupList = useSelector((state: StoreType) => state.projectGroup.groupList)

    const groupIds = useSelector((state: StoreType) => state.accountPermissions.groups.groupIds)
    const groupSortColumn = useSelector((state: StoreType) => state.accountPermissions.groups.sortColumn)
    const groupSortOrder = useSelector((state: StoreType) => state.accountPermissions.groups.sortOrder)
    const groupFilters = useSelector((state: StoreType) => state.accountPermissions.groups.filters)
    const groupSelected = useSelector((state: StoreType) => state.accountPermissions.groups.selected)

    const projectGroupIds = useSelector((state: StoreType) => state.accountPermissions.projectGroups.groupIds)
    const projectGroupSortColumn = useSelector((state: StoreType) => state.accountPermissions.projectGroups.sortColumn)
    const projectGroupSortOrder = useSelector((state: StoreType) => state.accountPermissions.projectGroups.sortOrder)
    const projectGroupFilters = useSelector((state: StoreType) => state.accountPermissions.projectGroups.filters)
    const projectGroupSelected = useSelector((state: StoreType) => state.accountPermissions.projectGroups.selected)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(ACCOUNT_GROUPS)

    useEffect(() => {
        const sortedGroupIds = SortGroups(Object.values(groupList), groupSortColumn, groupSortOrder, groupFilters)
        const notAssignedGroups = sortedGroupIds.filter((id) => !permissionItem.accountGroups.includes(id))
        dispatch(setAccountPermissionsGroupIds(notAssignedGroups))
    }, [groupList, groupSortColumn, groupSortOrder, groupFilters])

    useEffect(() => {
        const sortedProjectGroupIds = SortProjectGroups(
            Object.values(projectGroupList),
            projectGroupSortColumn,
            projectGroupSortOrder,
            projectGroupFilters,
        )
        const notAssignedProjectGroups = sortedProjectGroupIds.filter((id) => !permissionItem.projectGroups.includes(id))
        dispatch(setAccountPermissionsProjectGroupIds(notAssignedProjectGroups))
    }, [projectGroupList, projectGroupSortColumn, projectGroupSortOrder, projectGroupFilters])

    const onSortGroups: SortRows = (column: keyof MtGroup, order) => {
        dispatch(setAccountPermissionsGroupSort(column, order))
    }
    const onSortProjectGroups: SortRows = (column: keyof MtProjectGroup, order) => {
        dispatch(setAccountPermissionsProjectGroupSort(column, order))
    }

    const onGroupFilter = (newFilters: GroupFilters) => {
        dispatch(setAccountPermissionsGroupFilters(newFilters))
        dispatch(setAccountPermissionsGroupSelected(null))
    }
    const onProjectGroupFilter = (newFilters: ProjectGroupFilters) => {
        dispatch(setAccountPermissionsProjectGroupFilters(newFilters))
        dispatch(setAccountPermissionsProjectGroupSelected(null))
    }

    const onSelect = () => {
        const newPermissionItem: ProjectPermissionItem = deepCopy(permissionItem)
        const { accountGroups, projectGroups } = newPermissionItem
        newPermissionItem.accountGroups = accountGroups.concat(groupSelected)
        newPermissionItem.projectGroups = projectGroups.concat(projectGroupSelected)

        dispatch(updateBaseProjectPermission(
            section,
            permissionId,
            permissionType,
            newPermissionItem,
        ))

        dispatch(closeModal())
        dispatch(setAccountPermissionsGroupSelected(null))
        dispatch(setAccountPermissionsProjectGroupSelected(null))
    }

    const groupColumns = GroupColumns(accountId)
    const projectGroupColumns = ProjectGroupColumns()

    return (
        <Container>
            <PageHeader>
                <PageTitle>{activeTab === ACCOUNT_GROUPS ? lang.selectAccountGroup_s : lang.selectProjectGroup_s}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === ACCOUNT_GROUPS}
                        onClick={() => setActiveTab(ACCOUNT_GROUPS)}
                    >
                        {lang.accountGroups}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === PROJECT_GROUPS}
                        onClick={() => setActiveTab(PROJECT_GROUPS)}
                    >
                        {lang.projectGroups}
                    </PageMenuButton>
                </PageMenu>

                <Tab show={activeTab === ACCOUNT_GROUPS} noGrow>
                    <TableSearchWrapper>
                        <GroupSearch
                            id="permissionGroupList"
                            filters={groupFilters}
                            onFilter={onGroupFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={groupColumns}
                        dataList={groupList}
                        dataIds={groupIds}
                        selectedIds={groupSelected}
                        sortColumn={groupSortColumn}
                        sortOrder={groupSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortGroups}
                    />
                </Tab>

                <Tab show={activeTab === PROJECT_GROUPS} noGrow>
                    <TableSearchWrapper>
                        <ProjectGroupSearch
                            id="permissionProjectGroupList"
                            filters={projectGroupFilters}
                            onFilter={onProjectGroupFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={projectGroupColumns}
                        dataList={projectGroupList}
                        dataIds={projectGroupIds}
                        selectedIds={projectGroupSelected}
                        sortColumn={projectGroupSortColumn}
                        sortOrder={projectGroupSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortProjectGroups}
                    />
                </Tab>

                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <Button variant="success" row onClick={() => onSelect()}>
                                {lang.select}
                            </Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default NotAssigned
